import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "@/context/LocationContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, ArrowRight, ArrowLeft, Building2, User, ShieldCheck, Store,
  Landmark, FileText, CheckCircle2, Upload, MapPin, X } from "lucide-react";

const STEPS = [
  { key: "business", label: "Business", icon: Building2 },
  { key: "owner", label: "Owner", icon: User },
  { key: "kyc", label: "KYC", icon: ShieldCheck },
  { key: "store", label: "Store", icon: Store },
  { key: "bank", label: "Bank", icon: Landmark },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "review", label: "Submit", icon: CheckCircle2 },
];

const CATEGORIES = [
  ["grocery", "Grocery & Staples"], ["fruits-vegetables", "Fruits & Vegetables"], ["dairy", "Dairy & Eggs"],
  ["bakery", "Bakery"], ["beverages", "Beverages"], ["snacks", "Snacks"],
  ["personal-care", "Personal Care"], ["household", "Household"],
];
const BUSINESS_TYPES = ["Proprietorship", "Partnership", "Private Limited", "LLP", "Individual"];

const DOCS = [
  ["doc_pan", "PAN Card", true], ["doc_aadhaar", "Aadhaar Card", true],
  ["doc_shop_photo", "Shop Photo / Storefront", true], ["doc_bank_proof", "Bank Passbook / Cheque", true],
  ["doc_gst", "GST Certificate (optional)", false],
];

const Field = ({ label, children, req }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-slate-600">{label}{req && <span className="text-red-500"> *</span>}</span>
    {children}
  </label>
);
const inputCls = "w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand";

export default function RetailerOnboarding() {
  const nav = useNavigate();
  const { retailerSignup } = useAuth();
  const { detectCurrent, detecting } = useLocation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    store_name: "", business_type: "Proprietorship", gstin: "", address: "", area: "", city: "", state: "", pincode: "", lat: null, lng: null,
    name: "", email: "", phone: "", alternate_phone: "", password: "",
    pan_number: "", aadhaar_number: "",
    category: "grocery", description: "", delivery_radius: 3, open_time: "08:00", close_time: "22:00",
    account_holder: "", account_number: "", ifsc: "", bank_name: "", upi_id: "",
    doc_pan: "", doc_aadhaar: "", doc_gst: "", doc_shop_photo: "", doc_bank_proof: "",
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const put = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const useMyLocation = async () => {
    try { const loc = await detectCurrent(); setF((p) => ({ ...p, lat: loc.lat, lng: loc.lng, city: p.city || loc.city, state: p.state || loc.state, pincode: p.pincode || loc.pincode })); toast.success("Location captured"); }
    catch { toast.error("Could not detect location. Please enter manually."); }
  };

  const uploadDoc = async (key, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/onboarding/upload", fd); put(key, `${process.env.REACT_APP_BACKEND_URL}${data.url}`); toast.success("Uploaded"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const validate = () => {
    const s = STEPS[step].key;
    if (s === "business" && (!f.store_name.trim() || !f.address.trim() || !f.city.trim() || !f.pincode.trim())) return "Fill business name, address, city and pincode";
    if (s === "owner") {
      if (!f.name.trim() || !f.email.trim() || !f.phone.trim() || f.password.length < 6) return "Fill owner name, email, phone and a 6+ char password";
      if (!/^\S+@\S+\.\S+$/.test(f.email)) return "Enter a valid email";
    }
    if (s === "kyc") {
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(f.pan_number.toUpperCase())) return "Enter a valid 10-char PAN (e.g. ABCDE1234F)";
      if (!/^\d{12}$/.test(f.aadhaar_number.replace(/\s/g, ""))) return "Enter a valid 12-digit Aadhaar number";
    }
    if (s === "store" && !f.category) return "Select a store category";
    if (s === "bank" && (!f.account_holder.trim() || !f.account_number.trim() || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(f.ifsc.toUpperCase()))) return "Fill account holder, number and a valid IFSC";
    if (s === "documents") { for (const [k, lbl, req] of DOCS) if (req && !f[k]) return `Please upload: ${lbl}`; }
    return null;
  };

  const next = () => { const err = validate(); if (err) return toast.error(err); setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setLoading(true);
    try {
      await retailerSignup({ ...f, pan_number: f.pan_number.toUpperCase(), ifsc: f.ifsc.toUpperCase(), delivery_radius: +f.delivery_radius || 3 });
      setStep(STEPS.length - 1);
      toast.success("Application submitted for review!");
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  const done = step === STEPS.length - 1;
  const s = STEPS[step].key;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center gap-2"><img src="/logo.png" alt="Grofkart" className="h-8 w-auto" /></Link>
          <Link to="/login" className="text-sm font-semibold text-brand">Already a partner? Login</Link>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-8" data-testid="retailer-onboarding-wizard">
        <div className="mb-2 text-center">
          <h1 className="font-head text-2xl font-black text-slate-900 sm:text-3xl">Become a Grofkart Partner</h1>
          <p className="mt-1 text-sm text-slate-500">Digitise your shop in a few guided steps.</p>
        </div>

        {/* Stepper */}
        <div className="mb-8 flex items-center justify-between overflow-x-auto pb-2">
          {STEPS.map((st, i) => {
            const active = i === step, complete = i < step;
            const Icon = st.icon;
            return (
              <React.Fragment key={st.key}>
                <div className="flex flex-col items-center gap-1" data-testid={`step-indicator-${st.key}`}>
                  <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-colors ${complete ? "bg-brand text-white" : active ? "bg-brand-soft text-brand ring-2 ring-brand" : "bg-slate-100 text-slate-400"}`}>
                    {complete ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className={`hidden text-[11px] font-semibold sm:block ${active || complete ? "text-slate-800" : "text-slate-400"}`}>{st.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`mx-1 h-0.5 flex-1 ${i < step ? "bg-brand" : "bg-slate-200"}`} />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
          {s === "business" && (
            <div className="space-y-4" data-testid="step-business">
              <h2 className="font-head text-lg font-bold text-slate-900">Business information</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Business / Shop name" req><input value={f.store_name} onChange={set("store_name")} data-testid="onb-store-name" className={inputCls} placeholder="e.g. Sharma Kirana Store" /></Field>
                <Field label="Business type" req><select value={f.business_type} onChange={set("business_type")} className={inputCls}>{BUSINESS_TYPES.map((b) => <option key={b}>{b}</option>)}</select></Field>
                <Field label="GSTIN (optional)"><input value={f.gstin} onChange={set("gstin")} className={inputCls} placeholder="15-digit GSTIN" /></Field>
                <Field label="Pincode" req><input value={f.pincode} onChange={set("pincode")} data-testid="onb-pincode" className={inputCls} placeholder="411038" /></Field>
                <Field label="Shop address" req><input value={f.address} onChange={set("address")} data-testid="onb-address" className={inputCls} placeholder="Shop no, street, landmark" /></Field>
                <Field label="Area / Locality"><input value={f.area} onChange={set("area")} className={inputCls} placeholder="e.g. Kothrud" /></Field>
                <Field label="City" req><input value={f.city} onChange={set("city")} data-testid="onb-city" className={inputCls} placeholder="Pune" /></Field>
                <Field label="State"><input value={f.state} onChange={set("state")} className={inputCls} placeholder="Maharashtra" /></Field>
              </div>
              <button onClick={useMyLocation} disabled={detecting} data-testid="onb-use-location" className="inline-flex items-center gap-2 rounded-xl border border-brand/40 bg-brand-soft px-4 py-2 text-sm font-semibold text-brand">
                {detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Use my current location {f.lat && "✓"}
              </button>
            </div>
          )}

          {s === "owner" && (
            <div className="space-y-4" data-testid="step-owner">
              <h2 className="font-head text-lg font-bold text-slate-900">Owner information</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Owner full name" req><input value={f.name} onChange={set("name")} data-testid="onb-owner-name" className={inputCls} placeholder="Full name" /></Field>
                <Field label="Email (login)" req><input type="email" value={f.email} onChange={set("email")} data-testid="onb-email" className={inputCls} placeholder="you@example.com" /></Field>
                <Field label="Mobile number" req><input value={f.phone} onChange={set("phone")} data-testid="onb-phone" className={inputCls} placeholder="10-digit mobile" /></Field>
                <Field label="Alternate number"><input value={f.alternate_phone} onChange={set("alternate_phone")} className={inputCls} placeholder="Optional" /></Field>
                <Field label="Create password" req><input type="password" value={f.password} onChange={set("password")} data-testid="onb-password" className={inputCls} placeholder="Min 6 characters" /></Field>
              </div>
            </div>
          )}

          {s === "kyc" && (
            <div className="space-y-4" data-testid="step-kyc">
              <h2 className="font-head text-lg font-bold text-slate-900">KYC verification</h2>
              <p className="text-sm text-slate-500">Your details are used only for verification and kept secure.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="PAN number" req><input value={f.pan_number} onChange={(e) => put("pan_number", e.target.value.toUpperCase())} data-testid="onb-pan" maxLength={10} className={inputCls} placeholder="ABCDE1234F" /></Field>
                <Field label="Aadhaar number" req><input value={f.aadhaar_number} onChange={set("aadhaar_number")} data-testid="onb-aadhaar" maxLength={12} className={inputCls} placeholder="12-digit Aadhaar" /></Field>
              </div>
            </div>
          )}

          {s === "store" && (
            <div className="space-y-4" data-testid="step-store">
              <h2 className="font-head text-lg font-bold text-slate-900">Store details</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Primary category" req><select value={f.category} onChange={set("category")} data-testid="onb-category" className={inputCls}>{CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                <Field label="Delivery radius (km)"><input type="number" value={f.delivery_radius} onChange={set("delivery_radius")} className={inputCls} min={1} max={15} /></Field>
                <Field label="Opening time"><input type="time" value={f.open_time} onChange={set("open_time")} className={inputCls} /></Field>
                <Field label="Closing time"><input type="time" value={f.close_time} onChange={set("close_time")} className={inputCls} /></Field>
              </div>
              <Field label="Short store description"><textarea value={f.description} onChange={set("description")} rows={3} className={inputCls} placeholder="Tell customers what makes your store special" /></Field>
            </div>
          )}

          {s === "bank" && (
            <div className="space-y-4" data-testid="step-bank">
              <h2 className="font-head text-lg font-bold text-slate-900">Bank details for payouts</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Account holder name" req><input value={f.account_holder} onChange={set("account_holder")} data-testid="onb-acc-holder" className={inputCls} /></Field>
                <Field label="Account number" req><input value={f.account_number} onChange={set("account_number")} data-testid="onb-acc-number" className={inputCls} /></Field>
                <Field label="IFSC code" req><input value={f.ifsc} onChange={(e) => put("ifsc", e.target.value.toUpperCase())} data-testid="onb-ifsc" maxLength={11} className={inputCls} placeholder="HDFC0001234" /></Field>
                <Field label="Bank name"><input value={f.bank_name} onChange={set("bank_name")} className={inputCls} /></Field>
                <Field label="UPI ID (optional)"><input value={f.upi_id} onChange={set("upi_id")} className={inputCls} placeholder="name@bank" /></Field>
              </div>
            </div>
          )}

          {s === "documents" && (
            <div className="space-y-4" data-testid="step-documents">
              <h2 className="font-head text-lg font-bold text-slate-900">Upload documents</h2>
              <p className="text-sm text-slate-500">JPG / PNG / PDF, up to 5MB each.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {DOCS.map(([k, lbl, req]) => (
                  <div key={k} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700">{lbl}{req && <span className="text-red-500"> *</span>}</span>
                      {f[k] ? <button onClick={() => put(k, "")} className="text-slate-300 hover:text-red-500"><X className="h-4 w-4" /></button> : null}
                    </div>
                    {f[k] ? (
                      <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-brand"><CheckCircle2 className="h-4 w-4" /> Uploaded</div>
                    ) : (
                      <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-3 text-xs font-semibold text-slate-500 hover:border-brand hover:text-brand" data-testid={`onb-upload-${k}`}>
                        <Upload className="h-4 w-4" /> Choose file
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadDoc(k, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {s === "review" && !loading && (
            <div className="py-6 text-center" data-testid="step-review">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-soft text-brand"><CheckCircle2 className="h-9 w-9" /></div>
              <h2 className="mt-4 font-head text-2xl font-black text-slate-900">Application submitted!</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                Thanks, <b>{f.name || "partner"}</b>. Your store <b>{f.store_name}</b> is now pending admin approval.
                Once approved, you'll pay a one-time <b>₹11 verification fee</b> to activate your <b>3-month free trial</b> and go live.
              </p>
              <button onClick={() => nav("/retailer")} data-testid="onb-go-dashboard" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-bold text-white hover:bg-brand-deep">
                Go to my dashboard <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Nav buttons (hidden on final success screen) */}
          {!done && (
            <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
              <button onClick={back} disabled={step === 0} data-testid="onb-back" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-40">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {s === "documents" ? (
                <button onClick={submit} disabled={loading} data-testid="onb-submit" className="inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />} Submit application
                </button>
              ) : (
                <button onClick={next} data-testid="onb-next" className="inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-deep">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
